import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ROLE_LABELS } from "../lib/constants";
import { ScrollText, Mail, FileText, RefreshCw } from "lucide-react";

const ENTITY_FILTERS = ["all", "lead", "proposal", "work_order", "project", "document", "task", "user", "email", "permissions", "invoice"];

const ACTION_COLORS = {
  POST: "bg-emerald-100 text-emerald-800", PUT: "bg-sky-100 text-sky-800",
  DELETE: "bg-rose-100 text-rose-800", email: "bg-violet-100 text-violet-800",
  create: "bg-emerald-100 text-emerald-800", update: "bg-sky-100 text-sky-800", generate: "bg-amber-100 text-amber-800",
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async (f) => {
    setLoading(true);
    try {
      const q = f && f !== "all" ? `?entity_type=${f}` : "";
      setItems((await api.get(`/audit${q}`)).data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(filter); }, [filter]);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Governance</p>
          <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Audit Log</h1>
        </div>
        <button onClick={() => load(filter)} data-testid="audit-refresh-btn" className="flex items-center gap-2 bg-stone-200 text-[#0F2A4A] px-4 py-2 rounded-lg text-sm font-medium hover:bg-stone-300">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {ENTITY_FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`audit-filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition ${filter === f ? "bg-[#0F2A4A] text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-400"}`}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Entity</th>
              <th className="px-4 py-3">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-stone-50/60">
                <td className="px-4 py-2.5 text-xs text-stone-500 whitespace-nowrap">{new Date(a.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-medium text-stone-900">{a.user_name || "System"}</div>
                  <div className="text-[10px] text-stone-400">{ROLE_LABELS[a.user_role] || a.user_role || ""}</div>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${ACTION_COLORS[a.action] || "bg-stone-100 text-stone-600"}`}>{a.action}</span>
                </td>
                <td className="px-4 py-2.5 text-xs text-stone-600 capitalize flex items-center gap-1.5">
                  {a.entity_type === "email" ? <Mail className="w-3 h-3 text-violet-500" /> : <FileText className="w-3 h-3 text-stone-400" />}
                  {(a.entity_type || "").replace("_", " ")}
                </td>
                <td className="px-4 py-2.5 text-xs text-stone-600 max-w-md truncate" title={a.summary}>{a.summary}</td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-stone-400"><ScrollText className="w-8 h-8 mx-auto mb-2 opacity-40" /> No audit entries</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
